import React, { useState, useCallback, useRef } from "react";
import "./DiyImage.css";

/** 网格吸附：一定要用 py 算 gy，否则画线会出错！ */
function snapToGrid(px, py, cellSize) {
    return {
        gx: Math.round(px / cellSize),
        gy: Math.round(py / cellSize),
    };
}

/** 网格坐标 -> 像素坐标 */
function gridToPixel({ gx, gy }, cellSize) {
    return {
        x: gx * cellSize,
        y: gy * cellSize,
    };
}

/**
 * shape:
 * 1) 线段(line)： { type:"line", id, p1:{gx,gy}, p2:{gx,gy} }
 * 2) 弧/折线(arc)： {
 *      type: "arc", id,
 *      center: {x,y},   // 交点(像素)
 *      p1: {x,y}, p2:{x,y}, // 两次点击选的点(像素)
 *      isRightAngle: boolean,
 *      isLargeArc: boolean, // 是否大弧
 *      startAngle, endAngle // 仅对非直角的弧有效
 *      radius: number       // 仅对非直角的弧(我们取CP1作为r)
 *    }
 * 3) 文字(text)： { type: "text", id, x, y, value }
 * 4) 曲线(curve)： { type: "curve", id, points: [{x, y}, {x, y},...] }
 * 5) 圆形(circle)： { type: "circle", id, center: {x, y}, radius }
 * 6) 椭圆(ellipse)： { type: "ellipse", id, center: {x, y}, rx, ry }
 */
export default function DiyImage() {
    // 宽高改为可输入控制
    const [svgWidth, setSvgWidth] = useState(320);
    const [svgHeight, setSvgHeight] = useState(240);
    const [cellSize, setCellSize] = useState(20);

    // 当前工具：line(两点画线)、arc(两条线段上选点)、text(添加文字)、move(拖拽文字)、curve(画曲线)、circle(画圆)、ellipse(画椭圆)
    const [tool, setTool] = useState("line");

    // 已有图形集合
    const [shapes, setShapes] = useState([]);

    // 画线模式：临时存储网格点
    const [tempLinePoints, setTempLinePoints] = useState([]);

    // 画弧模式：临时存储两次点击 => { lineId, px, py }
    const [tempArcPoints, setTempArcPoints] = useState([]);

    // 画曲线模式：临时存储鼠标拖拽点
    const [tempCurvePoints, setTempCurvePoints] = useState([]);

    // 画圆形模式：临时存储圆心和起始点
    const [tempCirclePoints, setTempCirclePoints] = useState([]);

    // 画椭圆模式：临时存储圆心、长半轴端点和短半轴端点
    const [tempEllipsePoints, setTempEllipsePoints] = useState([]);

    // 标记是否正在画曲线
    const [isDrawingCurve, setIsDrawingCurve] = useState(false);

    // 各种 ID 计数
    const [lineCount, setLineCount] = useState(1);
    const [arcCount, setArcCount] = useState(1);
    const [textCount, setTextCount] = useState(1);
    const [curveCount, setCurveCount] = useState(1);
    const [circleCount, setCircleCount] = useState(1);
    const [ellipseCount, setEllipseCount] = useState(1);

    // 拖拽相关
    const [draggingTextId, setDraggingTextId] = useState(null);
    const dragStart = useRef({ x: 0, y: 0, shapeX: 0, shapeY: 0 });

    // 线条样式：dashed 或 solid
    const [lineStyle, setLineStyle] = useState('solid');

    const undo = () => {
        if (shapes.length > 0) {
            setShapes(prevShapes => prevShapes.slice(0, -1));
        }
    };

    // 用于复用：更改画布尺寸后，清空内容并重置计数
    const resetCanvas = () => {
        setShapes([]);
        setLineCount(1);
        setArcCount(1);
        setTextCount(1);
        setCurveCount(1);
        setCircleCount(1);
        setEllipseCount(1);
    };

    // 封装通用的尺寸改变方法
    const handleDimensionChange = (e, setDimension) => {
        const val = Math.max(160, +e.target.value);
        setDimension(val);
        // 清空内容并重置计数
        resetCanvas();
    };

    // -----------------------------------------
    // 鼠标按下
    // -----------------------------------------
    const handleMouseDown = useCallback((e) => {
        const { offsetX, offsetY } = e.nativeEvent;

        // 如果是“移动”工具，不执行画线/画弧/文字逻辑
        if (tool === "move") return;

        // ========== 文字模式 ==========
        if (tool === "text") {
            const textValue = prompt("请输入文字：");
            if (textValue) {
                const gpt = snapToGrid(offsetX, offsetY, cellSize);
                const newTextShape = {
                    type: "text",
                    id: "T" + textCount,
                    x: gpt.gx * cellSize,
                    y: gpt.gy * cellSize,
                    value: textValue,
                };
                setShapes([...shapes, newTextShape]);
                setTextCount(textCount + 1);
            }
            return;
        }

        // ========== 画线 ==========
        if (tool === "line") {
            const gpt = snapToGrid(offsetX, offsetY, cellSize);
            const newLinePts = [...tempLinePoints, gpt];
            if (newLinePts.length === 2) {
                // 生成线段
                const newLine = {
                    type: "line",
                    id: "L" + lineCount,
                    p1: newLinePts[0],
                    p2: newLinePts[1],
                    style: lineStyle,
                };
                setShapes([...shapes, newLine]);
                setLineCount(lineCount + 1);
                setTempLinePoints([]);
            } else {
                setTempLinePoints(newLinePts);
            }
            return;
        }

        // ========== 画曲线 ==========
        if (tool === "curve") {
            if (isDrawingCurve) {
                // 结束画曲线
                if (tempCurvePoints.length > 0) {
                    const newCurve = {
                        type: "curve",
                        id: "C" + curveCount,
                        points: tempCurvePoints,
                        style: lineStyle,
                    };
                    setShapes([...shapes, newCurve]);
                    setCurveCount(curveCount + 1);
                    setTempCurvePoints([]);
                    setIsDrawingCurve(false);
                }
            } else {
                // 开始画曲线
                setTempCurvePoints([{ x: offsetX, y: offsetY }]);
                setIsDrawingCurve(true);
            }
            return;
        }

        // ========== 画弧(或直角) ==========
        if (tool === "arc") {
            const lineId = findClosestLine(offsetX, offsetY, shapes, cellSize, 5);
            if (!lineId) return;

            const lineShape = shapes.find((sh) => sh.type === "line" && sh.id === lineId);
            if (!lineShape) return;

            // 投影到线段
            const proj = getProjectionOnLine(offsetX, offsetY, lineShape, cellSize);
            const arcPoints = [...tempArcPoints, { lineId, px: proj.x, py: proj.y }];
            if (arcPoints.length === 2) {
                // 已选两点 => 画弧或直角
                makeArcOrCorner(arcPoints[0], arcPoints[1]);
                setTempArcPoints([]);
            } else {
                setTempArcPoints(arcPoints);
            }
        }

        // ========== 画圆形 ==========
        if (tool === "circle") {
            const gpt = snapToGrid(offsetX, offsetY, cellSize);
            const newCirclePts = [...tempCirclePoints, { x: gpt.gx * cellSize, y: gpt.gy * cellSize }];
            if (newCirclePts.length === 2) {
                const center = newCirclePts[0];
                const radius = Math.sqrt((newCirclePts[1].x - center.x) ** 2 + (newCirclePts[1].y - center.y) ** 2);
                const newCircle = {
                    type: "circle",
                    id: "O" + circleCount,
                    center,
                    radius,
                    style: lineStyle,
                };
                setShapes([...shapes, newCircle]);
                setCircleCount(circleCount + 1);
                setTempCirclePoints([]);
            } else {
                setTempCirclePoints(newCirclePts);
            }
            return;
        }

        // ========== 画椭圆 ==========
        if (tool === "ellipse") {
            const gpt = snapToGrid(offsetX, offsetY, cellSize);
            const newEllipsePts = [...tempEllipsePoints, { x: gpt.gx * cellSize, y: gpt.gy * cellSize }];
            if (newEllipsePts.length === 3) {
                const center = newEllipsePts[0];
                const rx = Math.sqrt((newEllipsePts[1].x - center.x) ** 2 + (newEllipsePts[1].y - center.y) ** 2);
                const ry = Math.sqrt((newEllipsePts[2].x - center.x) ** 2 + (newEllipsePts[2].y - center.y) ** 2);
                const newEllipse = {
                    type: "ellipse",
                    id: "E" + ellipseCount,
                    center,
                    rx,
                    ry,
                    style: lineStyle,
                };
                setShapes([...shapes, newEllipse]);
                setEllipseCount(ellipseCount + 1);
                setTempEllipsePoints([]);
            } else {
                setTempEllipsePoints(newEllipsePts);
            }
            return;
        }
    }, [tool, cellSize, shapes, tempLinePoints, tempArcPoints, tempCurvePoints, tempCirclePoints, tempEllipsePoints, lineCount, arcCount, textCount, curveCount, circleCount, ellipseCount, lineStyle, isDrawingCurve]);

    // -----------------------------------------
    // 鼠标移动（用于拖拽文字和画曲线）
    // -----------------------------------------
    const handleMouseMove = useCallback((e) => {
        if (draggingTextId) {
            if (tool!== "move") return;
            const { offsetX, offsetY } = e.nativeEvent;
            const dx = offsetX - dragStart.current.x;
            const dy = offsetY - dragStart.current.y;

            setShapes((prevShapes) => prevShapes.map((sh) => {
                if (sh.type === "text" && sh.id === draggingTextId) {
                    return {
                        ...sh,
                        x: dragStart.current.shapeX + dx,
                        y: dragStart.current.shapeY + dy,
                    };
                }
                return sh;
            }));
        } else if (tool === "curve" && isDrawingCurve) {
            const { offsetX, offsetY } = e.nativeEvent;
            const newPoints = [...tempCurvePoints, { x: offsetX, y: offsetY }];
            setTempCurvePoints(newPoints);
        }
    }, [draggingTextId, tool, tempCurvePoints, isDrawingCurve]);

    // -----------------------------------------
    // 鼠标抬起（结束拖拽和画曲线）
    // -----------------------------------------
    const handleMouseUp = useCallback(() => {
        if (draggingTextId) {
            setDraggingTextId(null);
        }
        if (tool === "curve") {
            if (tempCurvePoints.length > 0) {
                const newCurve = {
                    type: "curve",
                    id: "C" + curveCount,
                    points: tempCurvePoints,
                    style: lineStyle,
                };
                setShapes([...shapes, newCurve]);
                setCurveCount(curveCount + 1);
                setTempCurvePoints([]);
            }
        }
    }, [draggingTextId, tool, tempCurvePoints, curveCount, lineStyle]);

    // -----------------------------------------
    // 用选到的两个点，基于两条线段交点 => 画弧 or 直角
    // -----------------------------------------
    const makeArcOrCorner = (arcPt1, arcPt2) => {
        const L1 = shapes.find((sh) => sh.type === "line" && sh.id === arcPt1.lineId);
        const L2 = shapes.find((sh) => sh.type === "line" && sh.id === arcPt2.lineId);
        if (!L1 ||!L2) return;

        // 线段像素坐标
        const L1p1 = gridToPixel(L1.p1, cellSize);
        const L1p2 = gridToPixel(L1.p2, cellSize);
        const L2p1 = gridToPixel(L2.p1, cellSize);
        const L2p2 = gridToPixel(L2.p2, cellSize);

        // 计算交点
        const center = lineSegmentIntersection(L1p1, L1p2, L2p1, L2p2);
        if (!center) {
            console.log("这两条线段不相交(或交点不在段内)，无法画弧");
            return;
        }

        // 判断是否近似直角
        const v1 = { x: L1p2.x - L1p1.x, y: L1p2.y - L1p1.y };
        const v2 = { x: L2p2.x - L2p1.x, y: L2p2.y - L2p1.y };
        const angle12 = angleBetweenVectors(v1, v2);
        const isRightAngle = Math.abs(angle12 - 90) < 1e-4;

        // 生成 shape
        const newArcId = "A" + arcCount;
        const newArc = {
            type: "arc",
            id: newArcId,
            center,
            p1: { x: arcPt1.px, y: arcPt1.py },
            p2: { x: arcPt2.px, y: arcPt2.py },
            isRightAngle,
            isLargeArc: false,
            startAngle: 0,
            endAngle: 0,
            radius: 0,
            clickCount: 0, // 新增：初始化点击次数
            sweepFlag: 0   // 新增：初始化弧线绘制方向标志
        };

        if (isRightAngle) {
            // 直角 => 渲染时画矩形拐角的折线
        } else {
            // 非直角 => 强制以 P1, P2 为弧端点 (SVG arc命令)
            const r = distance(center, newArc.p1);

            // 计算极角
            const deg1 = angleDeg(center, newArc.p1);
            const deg2 = angleDeg(center, newArc.p2);
            let start = Math.min(deg1, deg2);
            let end = Math.max(deg1, deg2);
            let diff = end - start;
            if (diff > 180) {
                diff = 360 - diff;
                const tmp = start;
                start = end;
                end = tmp;
            }
            newArc.radius = r;
            newArc.startAngle = start;
            newArc.endAngle = end;
        }

        setShapes([...shapes, newArc]);
        setArcCount(arcCount + 1);
    };

    // -----------------------------------------
// 切换 弧线大弧/小弧 和 sweepFlag
// -----------------------------------------
    const toggleArcType = (arcId) => {
        setShapes((prev) => prev.map((sh) => {
            if (sh.type === "arc" && sh.id === arcId &&!sh.isRightAngle) {
                const newClickCount = sh.clickCount + 1;
                if (newClickCount % 2 === 1) {
                    // 第 1、3、5... 次点击，切换 isLargeArc
                    return {
                        ...sh,
                        isLargeArc:!sh.isLargeArc,
                        clickCount: newClickCount
                    };
                } else {
                    // 第 2、4、6... 次点击，切换 sweepFlag
                    return {
                        ...sh,
                        sweepFlag: sh.sweepFlag === 0? 1 : 0,
                        clickCount: newClickCount
                    };
                }
            }
            return sh;
        }));
    };

// -----------------------------------------
// 保存 & 加载
// -----------------------------------------
    const saveShapes = async () => {
        const dataToSave = {
            type: "diy_image",
            shapes,
            svgWidth,
            svgHeight,
            cellSize
        };
        // localStorage.setItem("myShapes", JSON.stringify(dataToSave));

        // 将双引号替换为转义双引号
        const escapedData = JSON.stringify(dataToSave).replace(/"/g, '\\"');
        console.log(escapedData);
        try {
            // 将数据复制到剪贴板
            await navigator.clipboard.writeText(escapedData);
            console.log('数据已复制到剪贴板');
        } catch (error) {
            console.error('复制到剪贴板时出错:', error);
        }
    };


    const loadShapes = () => {
        const storedDataStr = '';
        try {
            const storedData = JSON.parse(storedDataStr);
            setShapes(storedData.shapes || []);
            setLineCount(storedData.lineCount || 1);
            setArcCount(storedData.arcCount || 1);
            setTextCount(storedData.textCount || 1);
            setCurveCount(storedData.curveCount || 1);
            setLineStyle(storedData.lineStyle ||'solid');
            setSvgWidth(storedData.svgWidth || 600);
            setSvgHeight(storedData.svgHeight || 400);
            setCellSize(storedData.cellSize || 20);
        } catch (err) {
            alert("图形数据解析错误！" + err);
        }
    };

    return (// 这里给容器加上行内样式，使其宽度与 svgWidth 同步
        <div
            className="diy-container"
            style={{ width: svgWidth + 50 + "px" }}
        >
            <div className="diy-toolbar">
                <label>
                    宽度:
                    <input
                        type="number"
                        value={svgWidth}
                        onChange={(e) => handleDimensionChange(e, setSvgWidth)}
                    />
                </label>
                <label>
                    高度:
                    <input
                        type="number"
                        value={svgHeight}
                        onChange={(e) => handleDimensionChange(e, setSvgHeight)}
                    />
                </label>
                <label>
                    网格大小:
                    <input
                        type="number"
                        value={cellSize}
                        onChange={(e) => setCellSize(Math.max(1, +e.target.value))}
                    />
                </label>

                <button onClick={undo} disabled={shapes.length === 0}>
                    撤回
                </button>

                <button
                    onClick={() => {
                        setTool("line");
                        setTempLinePoints([]);
                        setTempArcPoints([]);
                        setTempCurvePoints([]);
                        setTempCirclePoints([]);
                        setTempEllipsePoints([]);
                    }}
                    disabled={tool === "line"}
                >
                    画线
                </button>

                <button
                    onClick={() => {
                        setTool("curve");
                        setTempLinePoints([]);
                        setTempArcPoints([]);
                        setTempCurvePoints([]);
                        setTempCirclePoints([]);
                        setTempEllipsePoints([]);
                    }}
                    disabled={tool === "curve"}
                >
                    画曲线
                </button>

                <button
                    onClick={() => {
                        setTool("arc");
                        setTempLinePoints([]);
                        setTempArcPoints([]);
                        setTempCurvePoints([]);
                        setTempCirclePoints([]);
                        setTempEllipsePoints([]);
                    }}
                    disabled={tool === "arc"}
                >
                    画弧/直角
                </button>

                <button
                    onClick={() => {
                        setTool("text");
                        setTempLinePoints([]);
                        setTempArcPoints([]);
                        setTempCurvePoints([]);
                        setTempCirclePoints([]);
                        setTempEllipsePoints([]);
                    }}
                    disabled={tool === "text"}
                >
                    添加文字
                </button>

                <button
                    onClick={() => {
                        setTool("move");
                        setTempLinePoints([]);
                        setTempArcPoints([]);
                        setTempCurvePoints([]);
                        setTempCirclePoints([]);
                        setTempEllipsePoints([]);
                    }}
                    disabled={tool === "move"}
                >
                    移动文字
                </button>

                <button
                    onClick={() => {
                        setTool("circle");
                        setTempLinePoints([]);
                        setTempArcPoints([]);
                        setTempCurvePoints([]);
                        setTempCirclePoints([]);
                        setTempEllipsePoints([]);
                    }}
                    disabled={tool === "circle"}
                >
                    画圆
                </button>

                <button
                    onClick={() => {
                        setTool("ellipse");
                        setTempLinePoints([]);
                        setTempArcPoints([]);
                        setTempCurvePoints([]);
                        setTempCirclePoints([]);
                        setTempEllipsePoints([]);
                    }}
                    disabled={tool === "ellipse"}
                >
                    画椭圆
                </button>

                <select
                    value={lineStyle}
                    onChange={(e) => setLineStyle(e.target.value)}
                >
                    <option value="solid">实线</option>
                    <option value="dashed">虚线</option>
                </select>

                <button onClick={saveShapes}>保存</button>
                <button onClick={loadShapes}>加载</button>
            </div>

            <svg
                className="diy-svg"
                width={svgWidth}
                height={svgHeight}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
            >
                {/* 网格 */}
                <Grid width={svgWidth} height={svgHeight} cellSize={cellSize} />

                {/* 已完成图形(线/弧/文字/曲线/圆/椭圆) */}
                {shapes.map((sh) => {
                    if (sh.type === "line") {
                        return <LineShape key={sh.id} shape={sh} cellSize={cellSize} />;
                    } else if (sh.type === "arc") {
                        return (<ArcOrCorner key={sh.id} shape={sh} onClickArc={toggleArcType} />);
                    } else if (sh.type === "text") {
                        return (<TextShape
                            key={sh.id}
                            shape={sh}
                            tool={tool}
                            onDragStart={(e) => {
                                // 仅在“移动”工具时，允许拖拽
                                if (tool!== "move") return;
                                e.stopPropagation();
                                setDraggingTextId(sh.id);
                                dragStart.current.x = e.nativeEvent.offsetX;
                                dragStart.current.y = e.nativeEvent.offsetY;
                                dragStart.current.shapeX = sh.x;
                                dragStart.current.shapeY = sh.y;
                            }}
                        />);
                    } else if (sh.type === "curve") {
                        return <CurveShape key={sh.id} shape={sh} />;
                    } else if (sh.type === "circle") {
                        return <CircleShape key={sh.id} shape={sh} />;
                    } else if (sh.type === "ellipse") {
                        return <EllipseShape key={sh.id} shape={sh} />;
                    }
                    return null;
                })}

                {/* 画线模式：临时网格点 */}
                {tool === "line" && tempLinePoints.map((pt, i) => {
                    const { x, y } = gridToPixel(pt, cellSize);
                    return <circle key={i} className="temp-line-point" cx={x} cy={y} />;
                })}

                {/* 画弧模式：临时点(投影像素坐标) */}
                {tool === "arc" && tempArcPoints.map((obj, i) => (<circle
                    key={i}
                    className="temp-arc-point"
                    cx={obj.px}
                    cy={obj.py}
                />))}

                {/* 画曲线模式：临时曲线点 */}
                {tool === "curve" && tempCurvePoints.length > 0 && (
                    <CurveShape key="temp-curve" shape={{ type: "curve", points: tempCurvePoints, style: lineStyle }} />)}

                {/* 画圆模式：临时点 */}
                {tool === "circle" && tempCirclePoints.length === 1 && (
                    <circle key="temp-circle-center" className="temp-circle-point" cx={tempCirclePoints[0].x} cy={tempCirclePoints[0].y} />
                )}
                {tool === "circle" && tempCirclePoints.length === 2 && (
                    <CircleShape key="temp-circle" shape={{
                        type: "circle",
                        center: tempCirclePoints[0],
                        radius: Math.sqrt((tempCirclePoints[1].x - tempCirclePoints[0].x) ** 2 + (tempCirclePoints[1].y - tempCirclePoints[0].y) ** 2),
                        style: lineStyle
                    }} />
                )}

                {/* 画椭圆模式：临时点 */}
                {tool === "ellipse" && tempEllipsePoints.length === 1 && (
                    <circle key="temp-ellipse-center" className="temp-circle-point" cx={tempEllipsePoints[0].x} cy={tempEllipsePoints[0].y} />
                )}
                {tool === "ellipse" && tempEllipsePoints.length === 2 && (
                    <circle key="temp-ellipse-point1" className="temp-circle-point" cx={tempEllipsePoints[1].x} cy={tempEllipsePoints[1].y} />
                )}

            </svg>
        </div>);
}

// ------------------ 网格组件 ------------------
export function Grid({ width, height, cellSize, showGrid = true }) {
    if (!showGrid) return null; // 如果不显示网格，返回 null，避免渲染

    const lines = [];
    for (let x = 0; x <= width; x += cellSize) {
        lines.push(<line
            key={"v" + x}
            className="grid-line"
            x1={x}
            y1={0}
            x2={x}
            y2={height}
        />);
    }
    for (let y = 0; y <= height; y += cellSize) {
        lines.push(<line
            key={"h" + y}
            className="grid-line"
            x1={0}
            y1={y}
            x2={width}
            y2={y}
        />);
    }
    return <g>{lines}</g>;
}

// ------------------ 线段组件 ------------------
export function LineShape({ shape, cellSize }) {
    const { p1, p2, style } = shape;
    const pt1 = gridToPixel(p1, cellSize);
    const pt2 = gridToPixel(p2, cellSize);
    const strokeDasharray = style === 'dashed'? '5,5' : 'none';
    return (<line
        className="line-shape"
        x1={pt1.x}
        y1={pt1.y}
        x2={pt2.x}
        y2={pt2.y}
        strokeDasharray={strokeDasharray}
    />);
}

// ------------------ 弧 or 直角折线 ------------------
export function ArcOrCorner({ shape, onClickArc }) {
    const { id, center, p1, p2, isRightAngle, isLargeArc, radius, sweepFlag } = shape;

    if (isRightAngle) {
        // 直角 => 画“矩形”拐角的折线
        const p3 = {
            x: p2.x + (p1.x - center.x),
            y: p2.y + (p1.y - center.y),
        };
        const d = `M ${p1.x},${p1.y} L ${p3.x},${p3.y} L ${p2.x},${p2.y}`;
        return <path className="arc-shape" d={d} />;
    } else {
        // 非直角 => 画圆弧
        if (!radius || radius < 1e-9) return null;

        const largeArcFlag = isLargeArc? 1 : 0;
        const arcPath = [`M ${p1.x},${p1.y}`, `A ${radius},${radius} 0 ${largeArcFlag} ${sweepFlag} ${p2.x},${p2.y}`].join(" ");

        return (<path
            className="arc-shape"
            d={arcPath}
            onClick={(e) => {
                e.stopPropagation();
                onClickArc(id);
            }}
        />);
    }
}

// ------------------ 文字组件 ------------------
export function TextShape({ shape, onDragStart }) {
    const { x, y, value } = shape;
    return (<text
        className="text-shape"
        x={x}
        y={y}
        onMouseDown={onDragStart}
    >
        {value}
    </text>);
}

// ------------------ 曲线组件 ------------------
export function CurveShape({ shape }) {
    const { points, style } = shape;
    if (points.length < 2) return null;

    // 计算平滑贝塞尔曲线的路径
    const getSmoothBezierPath = (pts) => {
        let path = `M ${pts[0].x},${pts[0].y}`;

        for (let i = 1; i < pts.length - 1; i++) {
            const p0 = pts[i - 1];
            const p1 = pts[i];
            const p2 = pts[i + 1];

            // 计算控制点
            const dx1 = p1.x - p0.x;
            const dy1 = p1.y - p0.y;
            const dx2 = p2.x - p1.x;
            const dy2 = p2.y - p1.y;

            const controlPoint1X = p1.x - dx1 / 3;
            const controlPoint1Y = p1.y - dy1 / 3;
            const controlPoint2X = p1.x + dx2 / 3;
            const controlPoint2Y = p1.y + dy2 / 3;

            path += ` C ${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${p1.x},${p1.y}`;
        }

        // 处理最后一个点
        const lastPoint = pts[pts.length - 1];
        const secondLastPoint = pts[pts.length - 2];
        const lastDx = lastPoint.x - secondLastPoint.x;
        const lastDy = lastPoint.y - secondLastPoint.y;
        const lastControlPointX = secondLastPoint.x + lastDx / 3;
        const lastControlPointY = secondLastPoint.y + lastDy / 3;
        path += ` C ${lastControlPointX},${lastControlPointY} ${lastPoint.x},${lastPoint.y} ${lastPoint.x},${lastPoint.y}`;

        return path;
    };

    const d = getSmoothBezierPath(points);
    const strokeDasharray = style === 'dashed'? '5,5' : 'none';
    return (<path
        className="curve-shape"
        d={d}
        stroke="black"
        fill="none"
        strokeWidth="2"
        strokeDasharray={strokeDasharray}
    />);
}

// ------------------ 圆形组件 ------------------
export function CircleShape({ shape }) {
    const { center, radius, style } = shape;
    const strokeDasharray = style === 'dashed'? '5,5' : 'none';
    return (
        <circle
            className="circle-shape"
            cx={center.x}
            cy={center.y}
            r={radius}
            stroke={style === 'dashed'? 'black' : 'black'}
            fill="none"
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
        />
    );
}

// ------------------ 椭圆组件 ------------------
export function EllipseShape({ shape }) {
    const { center, rx, ry, style } = shape;
    const strokeDasharray = style === 'dashed'? '5,5' : 'none';
    return (
        <ellipse
            className="ellipse-shape"
            cx={center.x}
            cy={center.y}
            rx={rx}
            ry={ry}
            stroke={style === 'dashed'? 'black' : 'black'}
            fill="none"
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
        />
    );
}

// ------------------ 工具函数 ------------------

/** 线段相交(像素)，若在段内则返回交点{x,y}，否则null */
function lineSegmentIntersection(p1, p2, p3, p4) {
    const x1 = p1.x, y1 = p1.y;
    const x2 = p2.x, y2 = p2.y;
    const x3 = p3.x, y3 = p3.y;
    const x4 = p4.x, y4 = p4.y;

    const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    if (Math.abs(denom) < 1e-9) return null; // 平行或重合

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) return null; // 不在线段内

    return {
        x: x1 + ua * (x2 - x1),
        y: y1 + ua * (y2 - y1),
    };
}

/** 向量v1、v2的夹角(0~180)，单位度 */
function angleBetweenVectors(v1, v2) {
    const dot = v1.x * v2.x + v1.y * v2.y;
    const mag1 = Math.hypot(v1.x, v1.y);
    const mag2 = Math.hypot(v2.x, v2.y);
    if (mag1 < 1e-9 || mag2 < 1e-9) return 0;
    let c = dot / (mag1 * mag2);
    c = Math.max(-1, Math.min(1, c));
    const rad = Math.acos(c);
    const deg = (rad * 180) / Math.PI;
    return deg <= 180? deg : 360 - deg;
}

/** from->to 的极角(0~360) */
function angleDeg(from, to) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    let deg = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (deg < 0) deg += 360;
    return deg;
}

/** 两点距离 */
function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
}

/** 找到距离(px,py)小于maxDist的最近线段ID；没有就返回null */
function findClosestLine(px, py, shapes, cellSize, maxDist) {
    let bestId = null;
    let bestDist = Infinity;
    for (const sh of shapes) {
        if (sh.type!== "line") continue;
        const d = pointToSegmentDistance(px, py, sh, cellSize);
        if (d < maxDist && d < bestDist) {
            bestDist = d;
            bestId = sh.id;
        }
    }
    return bestId;
}

/** 点(px,py)到指定线段的最短距离(像素) */
function pointToSegmentDistance(px, py, lineShape, cellSize) {
    const p1 = gridToPixel(lineShape.p1, cellSize);
    const p2 = gridToPixel(lineShape.p2, cellSize);
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-9) {
        // 退化成点
        return Math.hypot(px - p1.x, py - p1.y);
    }
    let t = ((px - p1.x) * dx + (py - p1.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const projx = p1.x + t * dx;
    const projy = p1.y + t * dy;
    return Math.hypot(px - projx, py - projy);
}

/** 得到(px,py)在lineShape上的投影(像素)，若在线段外则夹到端点 */
function getProjectionOnLine(px, py, lineShape, cellSize) {
    const p1 = gridToPixel(lineShape.p1, cellSize);
    const p2 = gridToPixel(lineShape.p2, cellSize);
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-9) {
        // 退化成点
        return { x: p1.x, y: p1.y };
    }
    let t = ((px - p1.x) * dx + (py - p1.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return {
        x: p1.x + t * dx,
        y: p1.y + t * dy,
    };
}